import _extends from "@babel/runtime/helpers/extends";
import React, { createContext, forwardRef, useContext, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { propStyling, tombac } from "../../shared";
import { textStyles } from "../../shared/textStyles";

/** Context value type for FormField. */

/** Context for FormField components. */
const Context = /*#__PURE__*/createContext(null);

/**
 * Custom hook to use FormField context.
 *
 * @param componentName - Name of the component using the context.
 * @returns The FormField context value.
 * @throws Will throw an error if the component is not used inside
 *   FormField.Root.
 */
function useFormFieldContext(componentName) {
  const context = useContext(Context);
  if (!context) throw new Error(`FormField.${componentName} must be used inside FormField.Root`);
  return context;
}
/** Styled container for the root of the form field. */
const RootContainer = styled.div.withConfig({
  displayName: "FormField__RootContainer"
})(["all:initial;align-items:baseline;display:inline-grid;gap:", ";grid-template:", ";", ""], tombac.space(0, 2), _ref => {
  let {
    layout
  } = _ref;
  return layout === "inline" ? `
    'label input toolbar caption'
    'hint input toolbar caption'
    / max-content 1fr max-content max-content` : `
    'label toolbar'
    'hint hint'
    'input input'
    'caption caption'
    / 1fr max-content`;
}, propStyling);
/** Root component for the form field. */
const Root = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children,
    layout = "default",
    ...containerProps
  } = props;
  const [hasHint, setHasHint] = useState(false);
  return /*#__PURE__*/React.createElement(Context.Provider, {
    value: {
      hasHint,
      layout,
      setHasHint
    }
  }, /*#__PURE__*/React.createElement(RootContainer, _extends({}, containerProps, {
    layout: layout,
    ref: ref
  }), children));
});
Root.displayName = "FormField.Root";

/** Styled container for the label area. */
const LabelAreaContainer = styled.div.withConfig({
  displayName: "FormField__LabelAreaContainer"
})(["all:unset;align-self:", ";display:block;grid-area:label;min-height:", ";& > *{vertical-align:middle;}", ""], _ref2 => {
  let {
    hasHint,
    layout
  } = _ref2;
  return layout === "inline" && hasHint ? "flex-end" : undefined;
}, _ref3 => {
  let {
    layout
  } = _ref3;
  return layout === "inline" ? undefined : tombac.space(3);
}, propStyling);

/** Label area component for the form field. */
const LabelArea = /*#__PURE__*/forwardRef((props, ref) => {
  const context = useFormFieldContext("LabelArea");
  const {
    hasHint,
    layout
  } = context;
  return /*#__PURE__*/React.createElement(LabelAreaContainer, _extends({}, props, {
    hasHint: hasHint,
    layout: layout,
    ref: ref
  }));
});
LabelArea.displayName = "FormField.LabelArea";

/** Styled container for the content area. */
const ContentAreaContainer = styled.div.withConfig({
  displayName: "FormField__ContentAreaContainer"
})(["all:unset;display:flex;grid-area:input;", " ", ""], _ref4 => {
  let {
    layout
  } = _ref4;
  return layout !== "inline" && css(["&& > *{flex:auto;}"]);
}, propStyling);

/** Content area component for the form field. */
const ContentArea = /*#__PURE__*/forwardRef((props, ref) => {
  const context = useFormFieldContext("ContentArea");
  const {
    layout
  } = context;
  return /*#__PURE__*/React.createElement(ContentAreaContainer, _extends({}, props, {
    layout: layout,
    ref: ref
  }));
});
ContentArea.displayName = "FormField.ContentArea";
/** Styled container for the hint area. */
const HintAreaContainer = styled.div.withConfig({
  displayName: "FormField__HintAreaContainer"
})(["all:unset;align-self:", ";display:block;grid-area:hint;margin-bottom:", ";", " ", " ", ""], _ref5 => {
  let {
    layout
  } = _ref5;
  return layout === "inline" ? "flex-start" : undefined;
}, _ref6 => {
  let {
    layout
  } = _ref6;
  return layout === "inline" ? undefined : tombac.space(0.5);
}, tombac.typography.caption, textStyles, propStyling);

/** Hint area component for the form field. */
const HintArea = /*#__PURE__*/forwardRef((props, ref) => {
  const context = useFormFieldContext("HintArea");
  const {
    layout,
    hasHint,
    setHasHint
  } = context;
  useEffect(() => {
    if (hasHint) throw new Error("Only one HintArea is allowed per FormField");
    setHasHint === null || setHasHint === void 0 || setHasHint(true);
    return () => setHasHint === null || setHasHint === void 0 ? void 0 : setHasHint(false);
  }, []);
  return /*#__PURE__*/React.createElement(HintAreaContainer, _extends({
    variant: "secondary"
  }, props, {
    layout: layout,
    ref: ref
  }));
});
HintArea.displayName = "FormField.HintArea";
/** Toolbar area component for the form field. */
const ToolbarArea = styled.div.withConfig({
  displayName: "FormField__ToolbarArea"
})(["all:unset;display:block;grid-area:toolbar;", ""], propStyling);

/** Styled container for the caption area. */
const CaptionAreaContainer = styled.div.withConfig({
  displayName: "FormField__CaptionAreaContainer"
})(["all:unset;display:block;grid-area:caption;margin-top:", ";", " ", " ", ""], _ref7 => {
  let {
    layout
  } = _ref7;
  return layout === "inline" ? undefined : tombac.space(0.5);
}, tombac.typography.caption, textStyles, propStyling);
/** Caption area component for the form field. */
const CaptionArea = /*#__PURE__*/forwardRef((props, ref) => {
  const context = useFormFieldContext("CaptionArea");
  const {
    layout
  } = context;
  return /*#__PURE__*/React.createElement(CaptionAreaContainer, _extends({}, props, {
    layout: layout,
    ref: ref
  }));
});
CaptionArea.displayName = "FormField.CaptionArea";
export let FormFieldProps;
/**
 * A form field that groups a label, input, hint, toolbar, and caption. It is
 * used to create a consistent layout for form fields. Currently, it is only for
 * internal use, but it may be exposed in the future, replacing FormGroup.
 */
export const FormField = {
  /**
   * The root of a form field. All other `FormField.*` components must be
   * children of this component. For the field to work correctly, at least a
   * `LabelArea` and `ContentArea` must be present.
   *
   * ```tsx
   * return (
   *   <FormField.Root>
   *     <FormField.LabelArea>
   *       <Label htmlFor="input-id">Label</Label>
   *     </FormField.LabelArea>
   *     <FormField.ContentArea>
   *       <Input id="input-id" />
   *     </FormField.ContentArea>
   *   </FormField.Root>
   * );
   * ```
   *
   * There are two layout options for the form field: `default` and `inline`.
   * The default layout displays the label above the input, and the inline
   * layout displays the label to the left of the input. The layout can be set
   * using the `layout` prop.
   *
   * ```tsx
   * return <FormField.Root layout="inline">...</FormField.Root>;
   * ```
   */
  Root,
  /**
   * The label area of a form field. It is used to display the label of the
   * input.
   */
  LabelArea,
  /** The content area of a form field. It is used to display the input element. */
  ContentArea,
  /**
   * The optional hint area of a form field. It is used to provide additional
   * information about the input and is displayed below the label. Only one
   * HintArea is allowed per FormField.
   */
  HintArea,
  /**
   * The optional toolbar area of a form field. It is used to display additional
   * actions or information related to the input. It is displayed to the right
   * of the label in the default layout and to the right of the input in the
   * inline layout.
   */
  ToolbarArea,
  /**
   * The optional caption area of a form field. It is used to display additional
   * information about the input or to provide feedback about the input, e.g.,
   * an error message or a character count. It is displayed below the input in
   * the default layout and to the right of the input and toolbar in the inline
   * layout.
   */
  CaptionArea
};
//# sourceMappingURL=FormField.js.map